import type { CSSProperties } from "react";

import { DotRing } from "./dot-ring";
import { cn } from "@/lib/utils";

/** The three rings, innermost first: how far in each sits and its dots. */
const RINGS = [
  { inset: "25%", filled: 26, total: 36, dot: 2.1 },
  { inset: "12.5%", filled: 22, total: 54, dot: 1.5 },
  { inset: "0%", filled: 34, total: 72, dot: 1.1 },
];

/** How long the wave takes to pass from one ring to the next. */
const STEP_SECONDS = 0.45;

/**
 * One breath every seven seconds, most of it rest. A ring swells and brightens while its dots
 * grow a touch, then both settle. Every ring runs the same clock, each a beat later than
 * the one inside it, so the swell travels outward like a ripple instead of three rings pulsing
 * on their own. Dots scale around their own centres (`fill-box`), not the ring's. Under the breath
 * the whole set turns the same way, one lap in fifteen minutes: slow enough that you only notice
 * it has moved.
 */
const MOTION = `
@keyframes site-ring-breathe { 0%, 46%, 100% { scale: 1; opacity: 0.55; } 20% { scale: 1.05; opacity: 1; } }
@keyframes site-dot-breathe { 0%, 46%, 100% { scale: 1; } 20% { scale: 1.08; } }
@keyframes site-ring-turn { to { rotate: 1turn; } }
.site-rings > * { animation: site-ring-breathe 7s cubic-bezier(0.45, 0, 0.25, 1) var(--site-ring-delay) infinite both, site-ring-turn 900s linear infinite; }
.site-rings circle { transform-box: fill-box; transform-origin: center; animation: site-dot-breathe 7s cubic-bezier(0.45, 0, 0.25, 1) var(--site-ring-delay) infinite both; }
@media (prefers-reduced-motion: reduce) { .site-rings > *, .site-rings circle { animation: none; } }
`;

/**
 * The rings behind the landing's headline and every sub-page title. Decoration: they stand for
 * nothing. They breathe from the middle outward and turn very slowly. Position and size it from
 * outside; it holds still for reduced motion.
 */
export const BreathingRings = ({ className }: { className?: string }) => (
  <div aria-hidden className={cn("site-rings pointer-events-none absolute -z-10", className)}>
    <style>{MOTION}</style>
    {RINGS.map((ring, index) => (
      <DotRing
        key={ring.inset}
        filled={ring.filled}
        total={ring.total}
        dot={ring.dot}
        className="absolute"
        style={{ inset: ring.inset, "--site-ring-delay": `${index * STEP_SECONDS}s` } as CSSProperties}
      />
    ))}
  </div>
);
